import Vue from 'vue'
import { isLocal } from '../lib/helpers'
import logger from '../lib/logger'
import { useLocalStorage } from '@/lib/helpers/storage'

// NOTE: Some of our enterprises run on logo.com subdomains,
// so we need to be careful of breaking them
const preloadedDomains = ['app.logo.com', 'localhost']

const loadEnterpriseAsync = async ($axios, store, query, hostname) => {
  let enterprise
  // The handling of the EID is very specific:
  // If there is a query string with the EID, use that first as it's considered
  // an override.
  // As a last ditch, use the domain the user is on to fetch the EID.
  if (query.eid) {
    logger.debug(`Querying Enterprise by ID`)

    try {
      const { data: response } = await $axios.get(
        `/api/v2/enterprises/${query.eid}`
      )
      enterprise = response
    } catch (e) {
      if (e.response?.status === 404) {
        // The enterprise doesn't exist, so we'll fallback to the hostname
        return loadEnterpriseAsync($axios, store, {}, hostname)
      }
    }
  } else {
    logger.debug(`Querying Enterprise by domain`)

    const { data: response } = await $axios
      .get(`/api/v2/enterprises/query?domain=${hostname}`)
      .catch(() =>
        $axios.get(`/api/v2/enterprises/${store.state.enterprise.id}`)
      )

    enterprise = response
  }

  if (enterprise.id) {
    // In all cases, we stash this value for a faster lookup later.
    useLocalStorage().setItem('eid', enterprise.id)
  }

  store.commit('setEnterprise', enterprise)

  setMeta(enterprise)
}

export default async function({ $axios, store, query }) {
  logger.debug('Fetching EID')

  let { hostname } = window.location

  if (isLocal()) {
    logger.debug(
      'fetch-enterprise: Hostname set to devapplogi.logo.com while local'
    )
    hostname = 'devapp.logo.com'
  }

  const loadEnterprisePromise = loadEnterpriseAsync(
    $axios,
    store,
    query,
    hostname
  )

  // If we're on a preloaded domain, we can skip the fetch.
  // We also want to process synchronously if an eid is provided manually
  if (
    !query.eid &&
    preloadedDomains.some(domain => hostname.indexOf(domain) > -1)
  ) {
    loadEnterprisePromise.then(() =>
      logger.debug('Enterprise loaded fully async')
    )
  } else {
    await loadEnterprisePromise
  }
}

/*
 * Add Enterprise specific Meta data
 *
 * @param {object} enterprise
 */
function setMeta({ name, theme }) {
  Vue.use({
    install(Vue) {
      Vue.mixin({
        head: () => ({
          titleTemplate: `%s | ${name}`,
          meta: [
            {
              hid: 'description',
              name: 'description',
              content: `Make a logo for your brand in seconds with ${name}. Start building your business with a few clicks. 5M+ logos made. Create your brand today.`,
            },
            {
              hid: 'twitter:description',
              name: 'twitter:description',
              content: `Make a logo for your brand in seconds with ${name}. 5M+ logos made. Create your brand today.`,
            },
            { hid: 'og:url', property: 'og:url', content: theme.fallback_url },
            {
              hid: 'og:description',
              property: 'og:description',
              content: `Make a logo for your brand in seconds with ${name}. 5M+ logos made. Create your brand today.`,
            },
          ],
        }),
      })
    },
  })
}
